import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setProfile } from '../store/userSlice';
import {
  signIn,
  signInWithGoogleSupabase,
  signInWithFacebookSupabase,
  persistLoginQuestionnaireFields,
} from '../services/auth';
import { fetchMyProfile } from '../services/profile';
import googleIcon from '../assets/images/google.svg';
import facebookIcon from '../assets/images/facebook.svg';
import AuthPrimaryButton from './AuthPrimaryButton';
import AuthFormContainer from './AuthFormContainer';
import { getPostAuthHomeOrQuestionnaire } from '../utils/signInRedirect';

export default function SignInCard() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSignIn = async (e) => {
    e.preventDefault();
    try {
      setServerError('');
      const user = await signIn({ email, password });
      let merged = { ...user };
      try {
        const profile = await fetchMyProfile();
        if (typeof profile.medical_questionnaire_completed === 'boolean') {
          merged.medical_questionnaire_completed = profile.medical_questionnaire_completed;
        }
        if (
          profile.number_of_questions_filled !== null &&
          profile.number_of_questions_filled !== undefined &&
          Number.isFinite(Number(profile.number_of_questions_filled))
        ) {
          merged.number_of_questions_filled = Number(profile.number_of_questions_filled);
        }
        if (profile.questionnaire_completion_percentage != null) {
          merged.questionnaire_completion_percentage = profile.questionnaire_completion_percentage;
        }
        if (user.sections_completed != null) {
          merged.sections_completed = user.sections_completed;
        }
        if (user.section_progress != null) {
          merged.section_progress = user.section_progress;
        }
        if (user.totalSections != null) {
          merged.totalSections = user.totalSections;
        }
        if (profile.sections_completed != null) {
          merged.sections_completed = profile.sections_completed;
        }
        if (profile.section_progress != null) {
          merged.section_progress = profile.section_progress;
        }
        if (profile.totalSections != null) {
          merged.totalSections = profile.totalSections;
        }
        persistLoginQuestionnaireFields({
          medical_questionnaire_completed: merged.medical_questionnaire_completed,
          number_of_questions_filled: merged.number_of_questions_filled,
          questionnaire_completion_percentage: merged.questionnaire_completion_percentage,
          sections_completed: merged.sections_completed,
          section_progress: merged.section_progress,
          total_sections: merged.totalSections,
        });
      } catch (_) {
        // profile/me may fail before tokens propagate; login payload still applies
      }
      dispatch(
        setProfile({
          fullName: merged.fullName,
          email: merged.email,
          gender: merged.gender,
          dateOfBirth: merged.dateOfBirth,
          role: merged.role,
          organization: merged.organization,
          on_boarding_completed: merged.on_boarding_completed,
          on_boarding_step: merged.on_boarding_step,
          must_change_password: merged.must_change_password,
          ...(typeof merged.medical_questionnaire_completed === 'boolean'
            ? { medical_questionnaire_completed: merged.medical_questionnaire_completed }
            : {}),
          ...(merged.number_of_questions_filled !== null &&
          merged.number_of_questions_filled !== undefined &&
          Number.isFinite(Number(merged.number_of_questions_filled))
            ? { number_of_questions_filled: Number(merged.number_of_questions_filled) }
            : {}),
          ...(merged.questionnaire_completion_percentage != null
            ? { questionnaire_completion_percentage: merged.questionnaire_completion_percentage }
            : {}),
          ...(merged.sections_completed != null &&
          Number.isFinite(Number(merged.sections_completed))
            ? { sections_completed: Number(merged.sections_completed) }
            : {}),
          ...(merged.section_progress != null && Number.isFinite(Number(merged.section_progress))
            ? { section_progress: Number(merged.section_progress) }
            : {}),
          ...(merged.totalSections != null && Number.isFinite(Number(merged.totalSections))
            ? { totalSections: Number(merged.totalSections) }
            : {}),
        })
      );
      try {
        localStorage.setItem('userFullName', merged.fullName || '');
        localStorage.setItem('userEmail', merged.email || '');
        localStorage.setItem('userRole', merged.role || '');
        localStorage.setItem('userOrganization', merged.organization || '');
        if (typeof merged.on_boarding_completed === 'boolean') {
          localStorage.setItem('on_boarding_completed', String(merged.on_boarding_completed));
        }
        if (merged.on_boarding_step != null && merged.on_boarding_step !== '') {
          localStorage.setItem('on_boarding_step', merged.on_boarding_step);
        }
        localStorage.setItem(
          'must_change_password',
          String(Boolean(merged.must_change_password))
        );
      } catch (_) {
        // ignore localStorage errors (e.g., disabled or quota exceeded)
      }

      const needsPersonalDetails =
        merged.on_boarding_completed === false &&
        (merged.on_boarding_step === 'Personal Details' || String(merged.on_boarding_step || '').trim() === 'Personal Details');
      const needsBarcodeActivation =
        merged.on_boarding_completed === false &&
        ['bar code', 'barcode'].includes(String(merged.on_boarding_step || '').trim().toLowerCase());
      const onboardingIncomplete = merged.on_boarding_completed === false;
      const needsPasswordChange =
        onboardingIncomplete && merged.must_change_password === true;
      let viewAs = null;
      try {
        viewAs = localStorage.getItem('supervisorViewAsRole');
      } catch (_) {
        // ignore
      }
      const redirectPath = needsPasswordChange
        ? '/onboarding/change-password'
        : needsBarcodeActivation
        ? '/activate-barcode'
        : onboardingIncomplete || needsPersonalDetails
        ? '/personal-information'
        : getPostAuthHomeOrQuestionnaire(
            merged.medical_questionnaire_completed,
            merged.role,
            viewAs
          );
      navigate(redirectPath);
    } catch (err) {
      const apiErrors = err?.response?.data;
      const statusCode = err?.response?.status;
      const navigateToOTP = (extraState = {}) =>
        navigate('/otp-verification', {
          state: { email, ...extraState },
        });
      let message = 'Sign in failed. Please try again.';
      if (apiErrors?.non_field_errors?.length) {
        message = apiErrors.non_field_errors.join(' ');
        if (
          statusCode === 400 &&
          apiErrors.non_field_errors.some((errorMsg) =>
            errorMsg.includes('Please verify your email')
          )
        ) {
          setServerError(message);
          navigateToOTP();
          return;
        }
      } else if (apiErrors?.errors) {
        const errs = apiErrors.errors;
        const parts = [];
        if (errs.email)
          parts.push(
            Array.isArray(errs.email)
              ? errs.email.join(' ')
              : String(errs.email)
          );
        if (errs.password)
          parts.push(
            Array.isArray(errs.password)
              ? errs.password.join(' ')
              : String(errs.password)
          );
        if (parts.length) message = parts.join(' ');
      } else if (Array.isArray(apiErrors?.detail) && statusCode === 400) {
        message = apiErrors.detail.join(' ');
        const expiresRaw = apiErrors?.otp_expires_in?.[0];
        const expiresSeconds = Number(expiresRaw);
        if (
          apiErrors.detail.some((errorMsg) =>
            errorMsg.includes('Please verify your email')
          )
        ) {
          setServerError(message);
          const extraState =
            Number.isFinite(expiresSeconds) && expiresSeconds > 0
              ? { otp_expires_in: expiresSeconds }
              : {};
          navigateToOTP(extraState);
          return;
        }
      }
      setServerError(message);
    }
  };

  const handleGoogle = async () => {
    try {
      setServerError('');
      await signInWithGoogleSupabase();
    } catch (err) {
      setServerError(err?.message || 'Google sign in failed.');
    }
  };

  const handleFacebook = async () => {
    try {
      setServerError('');
      await signInWithFacebookSupabase();
    } catch (err) {
      setServerError(err?.message || 'Facebook sign in failed.');
    }
  };
  return (
    <AuthFormContainer>
      <div className="flex flex-col gap-1 md:gap-2 w-full">
        <p className="text-[#2B3133] font-libreBaskerville text-2xl md:text-3xl lg:text-4xl leading-[1em] text-center">
          Sign In
        </p>
        <p className="text-[#2B3133] font-poppins text-sm md:text-base font-normal text-center">
          Don&apos;t have an account?{' '}
          <a className="underline text-[#119975]" href="/signup">
            Create now
          </a>
        </p>
      </div>
      <form onSubmit={handleSignIn} className="flex flex-col gap-3 md:gap-4 mt-4 md:mt-5 w-full">
        <div className="flex flex-col gap-3 md:gap-4 w-full">
          <div className="flex flex-col gap-2">
            <label className="text-[#889696] font-poppins text-sm font-medium">
              E-mail
            </label>
            <input
              type="email"
              placeholder="example@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-4 md:px-5 outline-none font-poppins text-sm md:text-base text-[#2B3133]"
              required
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[#889696] font-poppins text-sm font-medium">
              Password
            </label>
            <div className="flex items-center w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="@#*%"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-1 bg-transparent outline-none px-4 md:px-5 pr-10 md:pr-12 font-poppins text-sm md:text-base text-[#2B3133]"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 w-10 md:w-12 h-full cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-center"
              >
                {showPassword ? (
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                  >
                    <path
                      d="M12.5 4.5C7.5 4.5 3.73 7.61 2.5 12C3.73 16.39 7.5 19.5 12.5 19.5C17.5 19.5 21.27 16.39 22.5 12C21.27 7.61 17.5 4.5 12.5 4.5ZM12.5 17C9.74 17 7.5 14.76 7.5 12C7.5 9.24 9.74 7 12.5 7C15.26 7 17.5 9.24 17.5 12C17.5 14.76 15.26 17 12.5 17ZM12.5 9C10.84 9 9.5 10.34 9.5 12C9.5 13.66 10.84 15 12.5 15C14.16 15 15.5 13.66 15.5 12C15.5 10.34 14.16 9 12.5 9Z"
                      fill="#889696"
                    />
                    <path
                      d="M2 2L22 22"
                      stroke="#889696"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                  >
                    <path
                      d="M10.2276 12C10.2276 12.6962 10.5041 13.3639 10.9964 13.8562C11.4887 14.3484 12.1564 14.625 12.8526 14.625C13.5487 14.625 14.2164 14.3484 14.7087 13.8562C15.201 13.3639 15.4776 12.6962 15.4776 12C15.4776 11.3038 15.201 10.6361 14.7087 10.1438C14.2164 9.65156 13.5487 9.375 12.8526 9.375C12.1564 9.375 11.4887 9.65156 10.9964 10.1438C10.5041 10.6361 10.2276 11.3038 10.2276 12ZM23.0291 11.3953C20.8072 6.71484 17.4486 4.35938 12.9463 4.35938C8.44161 4.35938 5.08537 6.71484 2.86349 11.3977C2.77437 11.5864 2.72815 11.7925 2.72815 12.0012C2.72815 12.2099 2.77437 12.416 2.86349 12.6047C5.08537 17.2852 8.44396 19.6406 12.9463 19.6406C17.451 19.6406 20.8072 17.2852 23.0291 12.6023C23.2096 12.2227 23.2096 11.782 23.0291 11.3953ZM12.8526 16.125C10.5744 16.125 8.72755 14.2781 8.72755 12C8.72755 9.72188 10.5744 7.875 12.8526 7.875C15.1307 7.875 16.9776 9.72188 16.9776 12C16.9776 14.2781 15.1307 16.125 12.8526 16.125Z"
                      fill="#889696"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-2">
          <label className="flex items-center gap-2 text-[#889696] font-poppins text-sm cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-[#CFD9E0] cursor-pointer"
            />
            Remember me
          </label>
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-[#2B3133] font-poppins text-sm font-medium underline hover:text-[#119975] transition-colors"
          >
            Forgot Password?
          </button>
        </div>
        {serverError ? (
          <p className="text-red-500 text-sm font-poppins -mt-2">
            {serverError}
          </p>
        ) : null}
        <AuthPrimaryButton type="submit" className="mt-2 md:mt-3">
          Sign In
        </AuthPrimaryButton>
      </form>
      <div className="my-4 md:my-5 w-full">
        <div className="flex items-center gap-3 md:gap-4">
          <div className="h-px bg-[#CBD5E0] flex-1" />
          <span className="text-[#889696] font-poppins text-xs font-medium">
            OR
          </span>
          <div className="h-px bg-[#CBD5E0] flex-1" />
        </div>
      </div>
      <div className="space-y-2 md:space-y-3 w-full">
        <button
          onClick={handleGoogle}
          type="button"
          className="w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-white flex items-center gap-3 text-[#889696] font-poppins px-5 md:px-6 hover:bg-gray-50 transition-colors"
        >
          <img src={googleIcon} alt="Google" className="w-5 h-5 md:w-6 md:h-6" />
          <span className="text-[#889696] font-poppins text-base md:text-lg font-medium flex-1 text-center">
            Continue with Google
          </span>
        </button>
        <button
          onClick={handleFacebook}
          type="button"
          className="w-full h-11 md:h-12 rounded-xl border border-[#CBD5E0] bg-white flex items-center gap-3 text-[#889696] font-poppins px-5 md:px-6 hover:bg-gray-50 transition-colors"
        >
          <img src={facebookIcon} alt="Facebook" className="w-5 h-5 md:w-6 md:h-6" />
          <span className="text-[#889696] font-poppins text-base md:text-lg font-medium flex-1 text-center">
            Continue with Facebook
          </span>
        </button>
      </div>
    </AuthFormContainer>
  );
}
